import React from 'react';

import { widgetsConfigurations } from 'config/widgetConfigurations/widgetsConfigurations';
import { WidgetView, WidgetViewModeUtils } from 'utilitys/reporting/widgetViewModeUtils';

import { useBaseWidget } from 'hooks/useBaseWidget';

import { Aggregation } from 'store/productivityRate.store';

import { ExportDropdown } from 'components/common/ExportDropdown/exportDropdown';
import { TooltipIcon } from 'components/common/TooltipIcon/tooltipIcon';
import { WidgetExistInBackend } from 'components/common/WidgetExistInBackend/WidgetExistInBackend';
import { WidgetTitle } from 'components/common/WidgetTitle/WidgetTitle';
import { WidgetWithTitle } from 'components/common/WidgetWithTitle/WidgetWithTitle';
import widgetWithTitleClasses from 'components/common/WidgetWithTitle/WidgetWithTitle.module.scss';
import { BarChart } from 'components/common/charts/barChart/barChart';

import classes from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/style/table.module.scss';
import { AddWidget } from 'components/Dashboards/Project/Components/CustomDashboard/AddRemoveWidget/addWidget';
import { IComponentProps } from 'components/Dashboards/Project/Components/CustomDashboard/utils/utils.type';
import { AddToReport } from 'components/Dashboards/Reporting/AddToReport/AddToReport';
import { ReportingVersionAndPills } from 'components/Dashboards/Reporting/ReportingVersionAndPills/ReportingVersionAndPills';
import { Narrative } from 'components/Dashboards/Reporting/WidgetNarrativeWrapper/Narrative';
import { PinboardPills } from 'components/Dashboards/Reporting/pinboardPills/PinboardPills';
import { ThreeDotsMenu } from 'components/Dashboards/Reporting/threeDotsMenu/ThreeDotsMenu';
import { Select } from 'components/DesignSystem/Select';
import { Boxes } from 'components/widgets/RunRateChart/components/boxes';
import { aggregationOptions } from 'components/widgets/RunRateChart/productivityRate.constants';
import { useProductivityRateLogic } from 'components/widgets/RunRateChart/productivityRate.logic';
import localClasses from 'components/widgets/RunRateChart/productivityRate.module.scss';
import { useProductivityRateProvider } from 'components/widgets/RunRateChart/productivityRate.provider';

const { componentKey, id, endpoint } = widgetsConfigurations.productivityRate;

export const ProductivityRate = ({
    projectId,
    contractId,
    widgetId,
    latestVersionId,
    compareVersionId,
    localFilters,
    globalFilterData,
    externalComponents = null,
    editNarrative = null,
    setSubTitleForExport,
    reportWidgetRef = null,
    widgetSelectedProjectAndContract = null,
    widgetViewMode = WidgetView.page,
    title = widgetsConfigurations.productivityRate.title,
    setIsPayloadReady,
}: IComponentProps) => {
    const { ui, api } = useProductivityRateProvider({
        widgetId,
        latestVersionId,
        localFilters,
        globalFilterData,
        compareVersionId,
        componentKey,
        widgetViewMode,
    });

    const { subTitle, route, editableTitle, setEditableTitle } = useBaseWidget({
        id,
        widgetId,
        projectId,
        contractId,
        latestVersionId,
        title,
        setSubTitleForExport,
        reportWidgetRef,
        ui,
        apiPayload: api.getProductivityRateData.payload,
        widgetEndpoint: endpoint,
    });

    const { logic } = useProductivityRateLogic({
        ui,
        api,
        title,
        subTitle,
        latestVersionId,
        widgetViewMode,
        setIsPayloadReady,
    });

    return (
        <WidgetExistInBackend componentKey={componentKey}>
            <WidgetWithTitle
                id={id}
                title={
                    <WidgetTitle title={editableTitle} setTitle={setEditableTitle} widgetViewMode={widgetViewMode} />
                }
                className={WidgetViewModeUtils.isReportView(widgetViewMode) ? widgetWithTitleClasses.inReport : ''}
                titleFilters={[
                    <ReportingVersionAndPills
                        key={'ReportingVersionAndPills'}
                        selectedProjectAndContract={widgetSelectedProjectAndContract}
                        pills={ui.pills}
                        widgetViewMode={widgetViewMode}
                    />,
                    <Narrative key={'narrative'} editNarrative={editNarrative} />,
                    <PinboardPills
                        key={'pinboardPills'}
                        isInPinboard={ui.isInPinboard}
                        pills={ui.pills}
                        widgetViewMode={widgetViewMode}
                    />,
                    WidgetViewModeUtils.isPageOrReportAddOrEditWidget(widgetViewMode) && (
                        <div data-testid={'filters'} key={'filters'} className={classes.filters}>
                            <div className={classes.selectWrapper}>
                                <div className={classes.title}>Aggregation</div>
                                <Select<ISelectOption<Aggregation>>
                                    value={aggregationOptions.find((item) => item.value === ui.state.aggregation)}
                                    options={aggregationOptions}
                                    onChange={(value) => value && ui.setters.onSetAggregation(value.value)}
                                />
                            </div>
                        </div>
                    ),
                ]}
                titleComponents={[
                    externalComponents && <div key={'externalComponents'}>{externalComponents}</div>,
                    <AddWidget
                        key={'AddWidget'}
                        componentKey={componentKey}
                        title={title}
                        projectId={projectId}
                        contractId={contractId}
                        widgetId={widgetId}
                        route={route}
                        localFilters={ui.state}
                        shouldHide={true}
                        shouldUseNewIcon={true}
                    />,
                    <AddToReport
                        key={'addWidgetToReport'}
                        widgetId={widgetId}
                        componentKey={componentKey}
                        projectId={projectId}
                        contractId={contractId}
                        uiState={ui.state}
                        shouldHide={WidgetViewModeUtils.isReportAddOrEditWidget(widgetViewMode)}
                        apiPayload={api.getProductivityRateData.payload}
                        widgetEndpoint={endpoint}
                    />,
                    !WidgetViewModeUtils.isReportAddOrEditWidget(widgetViewMode) && (
                        <ExportDropdown
                            key={'export'}
                            title={title}
                            subTitle={subTitle}
                            handleAPICsvExport={logic.handleAPICsvExport}
                        />
                    ),
                    <TooltipIcon key={'info'} tooltip={api.getProductivityRateData.instance.data?.info} />,
                    !WidgetViewModeUtils.isReportAddOrEditWidget(widgetViewMode) && (
                        <ThreeDotsMenu
                            widgetViewMode={widgetViewMode}
                            widgetId={widgetId}
                            componentKey={componentKey}
                            shareTitle={title}
                            shareLink={route}
                            projectId={projectId}
                            contractId={contractId}
                        />
                    ),
                ]}
            >
                <div className={localClasses.boxesAndChartWrapper}>
                    <Boxes data={api.getProductivityRateData.instance.data} ui={ui} />
                    <div className={localClasses.container}>
                        {logic.options && (
                            <BarChart
                                options={logic.options}
                                series={logic.series}
                                isFetching={api.getProductivityRateData.instance.isFetching}
                                noData={
                                    !api.getProductivityRateData.instance.isFetching &&
                                    !api.getProductivityRateData.instance.data
                                }
                                height={logic.chartHeight}
                            />
                        )}
                    </div>
                </div>
            </WidgetWithTitle>
        </WidgetExistInBackend>
    );
};
